import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { checkForActiveAuthSession } from 'actions';
import { invalidateAuthSessionAttempts } from 'actions/authSessionGeneration';
import useUnifiedLoginEnabled, {
  UNIFIED_LOGIN_DECISION_TIMEOUT_MS,
} from '../api/harness/useUnifiedLoginEnabled';

type Options = {
  /**
   * Check auth only for an inbound unified-login session. For routes that
   * never checked auth before unified login, so they stay that way while the
   * session-management flag is off.
   */
  requireInbound?: boolean;
};

/**
 * Dispatch `checkForActiveAuthSession` once the unified-login decision has
 * settled (or timed out), as an inbound check only when the session-management
 * flag is on. Returns whether the check ran as inbound.
 *
 * Inbound broker restore itself is deferred until the broker can hand back an
 * AMS candidate JWT (see the comment in `restoreExternalAuthSession`). Waiting
 * on the decision is free in the meantime, because `useUnifiedLoginEnabled`
 * reports ready immediately while the tenant environment is unconfigured.
 */
const useAuthSessionBootstrap = ({ requireInbound = false }: Options = {}): {
  inbound: boolean;
} => {
  const dispatch = useDispatch();
  const { isReady, sessionMgmtEnabled } = useUnifiedLoginEnabled();
  const [timedOut, setTimedOut] = useState(false);
  const [inbound, setInbound] = useState(false);
  const decided = useRef(false);
  const authCheckStarted = useRef(false);

  useEffect(() => {
    if (isReady) return undefined;
    const timeoutId = globalThis.setTimeout(
      () => setTimedOut(true),
      UNIFIED_LOGIN_DECISION_TIMEOUT_MS,
    );
    return () => globalThis.clearTimeout(timeoutId);
  }, [isReady]);

  useEffect(() => {
    if (decided.current || (!isReady && !timedOut)) {
      return;
    }
    decided.current = true;
    // A decision that missed its backstop is legacy, even if it lands later.
    const isInbound = isReady && !timedOut && sessionMgmtEnabled;
    setInbound(isInbound);
    if (requireInbound && !isInbound) return;
    authCheckStarted.current = true;
    dispatch(checkForActiveAuthSession({ inbound: isInbound }));
  }, [dispatch, isReady, timedOut, sessionMgmtEnabled, requireInbound]);

  // Mount-scoped on purpose: this must not run when the deps above change, or
  // a late decision would re-arm the latch the effect exists to hold. On a
  // route transition the successor's `beginAuthSessionAttempt` supersedes this
  // one anyway; the case that needs invalidating is leaving with no successor,
  // where an in-flight Checkr Profile response would otherwise land on an
  // unmounted route and write session storage. Resetting the refs lets a
  // remount — StrictMode's double-mount included — start a fresh attempt.
  useEffect(
    () => () => {
      decided.current = false;
      if (!authCheckStarted.current) return;
      invalidateAuthSessionAttempts();
      authCheckStarted.current = false;
    },
    [],
  );

  return { inbound };
};

export default useAuthSessionBootstrap;
